import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Switch,
  Divider,
  Grid,
  List,
  ListItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ExitToApp as ExitIcon,
  CloudUpload as UploadIcon,
  PhotoCamera as CameraIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { checklistResponseCountsAsDone, collectionHasCannotCompleteWithoutReason, hasCannotCompleteReason } from '../../utils/checklistItemUtils';
import ChecklistItemRenderer from './ChecklistItemRenderer';
import ChecklistReviewDialog from './ChecklistReviewDialog';

interface ChecklistInstance {
  id: number;
  checklist: number;
  instance_name: string;
  instance_identifier: string;
  location_details: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ChecklistItem {
  id: number;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules: any;
  options: string[];
  default_value: string;
  allowed_file_types: string;
  max_file_size_mb: number;
  is_visible: boolean;
  show_when_parent_completed: boolean;
}

interface Checklist {
  id: number;
  title: string;
  description: string;
  category: string;
  version: number;
  is_active: boolean;
  is_template: boolean;
  require_all_items: boolean;
  minimum_completion_percentage: number;
  total_items: number;
  required_items: number;
  has_instances: boolean;
  instance_count: number;
  created_at: string;
  updated_at: string;
  items: ChecklistItem[];
  instances: ChecklistInstance[];
}

interface Assignment {
  id: number;
  assignment_type: string;
  content_title: string;
  description: string;
  status: string;
  due_date: string;
  is_overdue: boolean;
  completion_count: number;
  is_completed: boolean;
  assigned_by: {
    email: string;
    first_name: string;
    last_name: string;
  };
  object_id: string;
  assignees: { id: number; email: string; first_name: string; last_name: string }[];
}

interface ItemResponse {
  item_id: number;
  instance_id?: number;
  response: any;
  notes: string;
  cannot_complete: boolean;
  cannot_complete_reason: string;
  file_uploads?: File[];
}

const ChecklistPlayer: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [responses, setResponses] = useState<{ [key: number]: ItemResponse }>({});
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [completionResults, setCompletionResults] = useState<any>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  
  // Instance management
  const [instances, setInstances] = useState<ChecklistInstance[]>([]);
  const [currentInstanceIndex, setCurrentInstanceIndex] = useState(0);
  const [instanceResponses, setInstanceResponses] = useState<{[instanceId: number]: {[itemId: number]: ItemResponse}}>({});
  const [showInstanceSidebar, setShowInstanceSidebar] = useState(true);

  // Memoized current responses based on instance
  const currentResponses = useMemo(() => {
    if (instances.length === 0) return responses;
    const currentInstance = instances[currentInstanceIndex];
    if (!currentInstance) return responses;
    return instanceResponses[currentInstance.id] || {};
  }, [instances, currentInstanceIndex, instanceResponses, responses]);

  // Load assignment and checklist data
  useEffect(() => {
    if (assignmentId) {
      loadAssignmentData();
    }
  }, [assignmentId]);

  // Reload responses after instances are loaded in read-only mode
  useEffect(() => {
    if (isReadOnly && instances.length > 0 && assignmentId && Object.keys(instanceResponses).length > 0) {
      // Instances are now loaded, and we have instanceResponses - update currentItemIndex
      const currentInstance = instances[currentInstanceIndex];
      if (currentInstance && instanceResponses[currentInstance.id]) {
        const firstIncompleteIndex = findFirstIncompleteItemIndexWithResponses(
          checklist?.items || [],
          instanceResponses[currentInstance.id]
        );
        setCurrentItemIndex(firstIncompleteIndex);
      }
    }
  }, [instances, isReadOnly, assignmentId, instanceResponses, currentInstanceIndex, checklist]);

  // Set page title
  useEffect(() => {
    if (checklist) {
      document.title = checklist.title;
    }
  }, [checklist]);

  const loadAssignmentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check for readonly parameter in URL
      const urlParams = new URLSearchParams(window.location.search);
      const isReadOnlyFromUrl = urlParams.get('readonly') === 'true';
      
      if (isReadOnlyFromUrl) {
        setIsReadOnly(true);
      }

      // Fetch assignment
      const assignmentResponse = await api.get(`/assignments/${assignmentId}/`);
      const assignmentData = assignmentResponse.data;
      setAssignment(assignmentData);

      // Check if assignment is completed by looking for completed AssignmentCompletion records
      let isAssignmentReadOnly = false;
      try {
        const assignmentCompletionResponse = await api.get(`/assignment-completions/?assignment=${assignmentId}`);
        const hasCompletedAssignment = assignmentCompletionResponse.data.results?.some((completion: any) => 
          completion.status === 'completed'
        );
        
        if (hasCompletedAssignment) {
          isAssignmentReadOnly = true;
          setIsReadOnly(true);
        }
      } catch (completionError) {
        console.log('No assignment completion found, assignment is editable.');
      }

      // Fetch checklist
      const checklistResponse = await api.get(`/checklists/${assignmentData.object_id}/`);
      const checklistData = checklistResponse.data;
      setChecklist(checklistData);
      
      // Load instances if the checklist has them
      let activeInstances: ChecklistInstance[] = [];
      if (checklistData.instances && checklistData.instances.length > 0) {
        activeInstances = checklistData.instances.filter((inst: ChecklistInstance) => inst.is_active);
        setInstances(activeInstances);
      }

      // Load existing responses if any - pass the read-only status and instances
      const loadedResponses = await loadExistingResponses(assignmentData.id, isAssignmentReadOnly || isReadOnlyFromUrl, activeInstances);

      // Start at first incomplete item using the loaded responses
      const firstIncompleteIndex = findFirstIncompleteItemIndexWithResponses(checklistData.items, loadedResponses);
      setCurrentItemIndex(firstIncompleteIndex);

    } catch (err) {
      console.error('Error loading assignment data:', err);
      setError('Failed to load checklist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingResponses = async (assignmentId: number, isReadOnlyMode: boolean = false, providedInstances: ChecklistInstance[] = []) => {
    try {
      console.log(`Loading existing responses for assignment ${assignmentId}, readOnly: ${isReadOnlyMode}`);
      
      // Use provided instances if available, otherwise use state instances
      const instancesToUse = providedInstances.length > 0 ? providedInstances : instances;
      
      // For read-only mode (completed assignments), always load from server first
      if (isReadOnlyMode) {
        console.log('Loading from server for read-only mode');
        const response = await api.get(`/assignment-completions/?assignment=${assignmentId}`);
        if (response.data.results && response.data.results.length > 0) {
          const completion = response.data.results[0];
          console.log('Found server completion:', completion);
          if (completion.completion_data) {
            // Helper to normalize responses - ensure keys are numbers and item_id is set
            const normalizeResponses = (responsesObj: any): { [itemId: number]: ItemResponse } => {
              const normalized: { [itemId: number]: ItemResponse } = {};
              Object.entries(responsesObj).forEach(([key, value]: [string, any]) => {
                const itemId = parseInt(key);
                if (!isNaN(itemId)) {
                  normalized[itemId] = {
                    item_id: value.item_id || itemId,
                    response: value.response,
                    cannot_complete: value.cannot_complete || false,
                    cannot_complete_reason: value.cannot_complete_reason || '',
                    notes: value.notes || '',
                    file_uploads: value.file_uploads || []
                  };
                }
              });
              return normalized;
            };

            // For multi-instance checklists, load instanceResponses
            if (completion.completion_data.instanceResponses) {
              console.log('Loaded server instanceResponses:', completion.completion_data.instanceResponses);
              // Normalize all instance responses
              const normalizedInstanceResponses: { [instanceId: number]: { [itemId: number]: ItemResponse } } = {};
              Object.entries(completion.completion_data.instanceResponses).forEach(([instanceIdStr, responses]: [string, any]) => {
                const instanceId = parseInt(instanceIdStr);
                if (!isNaN(instanceId)) {
                  normalizedInstanceResponses[instanceId] = normalizeResponses(responses);
                }
              });
              setInstanceResponses(normalizedInstanceResponses);
              // Return responses for the first instance (if instances are provided/loaded)
              if (instancesToUse.length > 0) {
                const firstInstance = instancesToUse[0];
                if (firstInstance && normalizedInstanceResponses[firstInstance.id]) {
                  return normalizedInstanceResponses[firstInstance.id];
                }
              }
              // Instances not loaded yet or no responses for first instance - return empty
              // The component will use currentResponses memo which will update once instances are loaded
              return {};
            }
            // For multi-instance checklists saved before instanceResponses support,
            // try to reconstruct from ChecklistItemResponse API using the completion id
            if (instancesToUse.length > 0) {
              try {
                const completionId = completion.id;
                const resp = await api.get(`/checklist-item-responses/?completion=${completionId}`);
                const results = resp.data.results || resp.data || [];
                if (Array.isArray(results) && results.length > 0) {
                  const reconstructed: { [instanceId: number]: { [itemId: number]: ItemResponse } } = {};
                  results.forEach((r: any) => {
                    const instanceId = r.instance || instancesToUse[0]?.id; // fallback to first instance if null
                    if (!instanceId) return;
                    if (!reconstructed[instanceId]) reconstructed[instanceId] = {};
                    const itemId = r.item;
                    reconstructed[instanceId][itemId] = {
                      item_id: itemId,
                      instance_id: instanceId,
                      response: r.response !== undefined ? r.response : r.response_value,
                      cannot_complete: !!r.cannot_complete,
                      cannot_complete_reason: r.cannot_complete_reason || '',
                      notes: r.notes || '',
                      file_uploads: r.file_uploads || []
                    };
                  });
                  if (Object.keys(reconstructed).length > 0) {
                    console.log('Reconstructed instanceResponses from item responses API');
                    setInstanceResponses(reconstructed);
                    const firstInstance = instancesToUse[0];
                    if (firstInstance && reconstructed[firstInstance.id]) {
                      return reconstructed[firstInstance.id];
                    }
                    return {};
                  }
                }
              } catch (e) {
                console.warn('Failed to reconstruct instance responses from API', e);
              }
            }
            // As last resort for multi-instance without instanceResponses, convert regular responses to first instance
            if (instancesToUse.length > 0 && completion.completion_data.responses) {
              console.log('Converting old format responses to instanceResponses for multi-instance checklist (fallback)');
              const savedResponses = normalizeResponses(completion.completion_data.responses);
              const convertedInstanceResponses: { [instanceId: number]: { [itemId: number]: ItemResponse } } = {};
              convertedInstanceResponses[instancesToUse[0].id] = savedResponses;
              setInstanceResponses(convertedInstanceResponses);
              return savedResponses;
            }
            // For single-instance checklists, load regular responses
            if (completion.completion_data.responses) {
              const savedResponses = normalizeResponses(completion.completion_data.responses);
              console.log('Loaded server responses:', savedResponses);
              setResponses(savedResponses);
              return savedResponses;
            }
          }
        }
        console.log('No server responses found for read-only mode');
        return {}; // Return empty object if no server responses found
      }

      // For editable mode (new assignments), only check localStorage
      // Do NOT fall back to server data as this could load responses from previous assignments
      const localStorageKey = `checklist_${assignmentId}`;
      console.log('Checking localStorage with key:', localStorageKey);
      const localStorageData = localStorage.getItem(localStorageKey);
      
      if (localStorageData) {
        console.log('Found localStorage data for assignment', assignmentId);
        try {
          const parsedData = JSON.parse(localStorageData);
          
          // Load instance responses if they exist
          if (parsedData.instanceResponses) {
            console.log('Loaded localStorage instanceResponses:', parsedData.instanceResponses);
            setInstanceResponses(parsedData.instanceResponses);
            // Return the responses for the first instance (or empty object)
            const firstInstance = instances[0];
            if (firstInstance && parsedData.instanceResponses[firstInstance.id]) {
              return parsedData.instanceResponses[firstInstance.id];
            }
            return {};
          }
          
          // Load non-instance responses if they exist
          if (parsedData.responses) {
            console.log('Loaded localStorage responses:', parsedData.responses);
            setResponses(parsedData.responses);
            return parsedData.responses;
          }
        } catch (parseError) {
          console.error('Error parsing localStorage data:', parseError);
          // If localStorage data is corrupted, remove it
          localStorage.removeItem(localStorageKey);
        }
      } else {
        console.log('No localStorage data found for assignment', assignmentId);
      }
      
      // For new assignments, return empty object - no existing responses
      console.log('Starting with empty responses for new assignment');
      return {};
    } catch (err) {
      console.error('Error loading existing responses:', err);
      return {};
    }
  };

  const getCurrentInstance = (): ChecklistInstance | null => {
    if (instances.length === 0) return null;
    return instances[currentInstanceIndex] || null;
  };

  const getCurrentResponses = (): { [key: number]: ItemResponse } => {
    const currentInstance = getCurrentInstance();
    if (!currentInstance) return responses;
    return instanceResponses[currentInstance.id] || {};
  };

  // Helper function to determine if an item response is complete
  const isItemResponseComplete = (response: ItemResponse | undefined, item: ChecklistItem): boolean => {
    if (!response) return false;
    
    // Cannot Complete counts only when the required reason is present.
    if (response.cannot_complete) return hasCannotCompleteReason(response.cannot_complete_reason);
    
    // Check based on response value
    const value = response.response;
    
    // Null or undefined means not answered
    if (value === null || value === undefined) return false;
    
    // For checkbox items, must be true
    if (item.item_type === 'checkbox') {
      return value === true;
    }
    
    // For text/number inputs, must have a non-empty value
    if (item.item_type === 'text_input' || item.item_type === 'number_input') {
      return typeof value === 'string' ? value.trim().length > 0 : value !== '';
    }
    
    // For select/radio, must have a value
    if (item.item_type === 'select' || item.item_type === 'radio') {
      return value !== '' && value !== null;
    }

    // For multi-select, must have at least one selected option
    if (item.item_type === 'multi_select') {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) && parsed.length > 0;
        } catch {
          return value.trim().length > 0;
        }
      }
      return false;
    }
    
    // For file uploads, check if file_uploads exist
    if (item.item_type === 'file_upload' || item.item_type === 'photo') {
      return (response.file_uploads && response.file_uploads.length > 0) || value !== null;
    }
    
    // For other types (date, time, signature, location), any non-null value is complete
    return value !== null && value !== '' && value !== undefined;
  };

  const isInstanceComplete = (instanceId: number): boolean => {
    if (!checklist || !checklist.items) return false;
    const instResponses = instanceResponses[instanceId] || {};
    const requiredItems = checklist.items.filter(item => item.is_required);
    return requiredItems.every(item => {
      const response = instResponses[item.id];
      return isItemResponseComplete(response, item);
    });
  };

  const getCompletedInstancesCount = (): number => {
    return instances.filter(inst => isInstanceComplete(inst.id)).length;
  };

  const findFirstIncompleteItemIndex = (items: ChecklistItem[]): number => {
    const currentResponses = getCurrentResponses();
    for (let i = 0; i < items.length; i++) {
      if (!currentResponses[items[i].id]) {
        return i;
      }
    }
    return 0; // If all items have responses, start at the beginning
  };

  const findFirstIncompleteItemIndexWithResponses = (items: ChecklistItem[], responses: any): number => {
    for (let i = 0; i < items.length; i++) {
      if (!responses[items[i].id]) {
        return i;
      }
    }
    return 0; // If all items have responses, start at the beginning
  };

  const getCurrentItem = (): ChecklistItem | null => {
    if (!checklist || !checklist.items || checklist.items.length === 0) {
      return null;
    }
    return checklist.items[currentItemIndex];
  };

  const getCompletionPercentage = (): number => {
    if (!checklist || !checklist.items) return 0;
    
    const currentResponses = getCurrentResponses();
    const totalItems = checklist.items.length;
    const completedItems = checklist.items.filter(item => {
      const response = currentResponses[item.id];
      return isItemResponseComplete(response, item);
    }).length;
    
    return Math.round((completedItems / totalItems) * 100);
  };

  const getRequiredCompletionPercentage = (): number => {
    if (!checklist || !checklist.items) return 0;
    
    const currentResponses = getCurrentResponses();
    const requiredItems = checklist.items.filter(item => item.is_required);
    const completedRequiredItems = requiredItems.filter(item => {
      const response = currentResponses[item.id];
      return isItemResponseComplete(response, item);
    }).length;
    
    return Math.round((completedRequiredItems / requiredItems.length) * 100);
  };

  const isChecklistComplete = (): boolean => {
    if (!checklist || !checklist.items) return false;
    
    // If using instances, check if all instances are complete
    if (instances.length > 0) {
      return instances.every(inst => isInstanceComplete(inst.id));
    }
    
    // Otherwise, check current responses
    const currentResponses = getCurrentResponses();
    const requiredItems = checklist.items.filter(item => item.is_required);
    return requiredItems.every(item => {
      const response = currentResponses[item.id];
      return isItemResponseComplete(response, item);
    });
  };

  const handleItemResponse = useCallback((itemId: number, response: any) => {
    if (isReadOnly) return; // Prevent editing in read-only mode
    
    const currentInstance = getCurrentInstance();
    
    if (currentInstance) {
      // Instance-based response handling
      setInstanceResponses(prev => {
        let newResponseData: ItemResponse = {
          ...prev[currentInstance.id]?.[itemId],
          item_id: itemId,
          instance_id: currentInstance.id,
          cannot_complete: false,
          cannot_complete_reason: '',
          notes: ''
        };

        // Handle different response types
        if (typeof response === 'object' && response !== null && response.file_uploads) {
          newResponseData = {
            ...newResponseData,
            response: response.response,
            file_uploads: response.file_uploads
          };
        } else {
          newResponseData = {
            ...newResponseData,
            response: response
          };
        }

        const newInstanceResponses = {
          ...prev,
          [currentInstance.id]: {
            ...prev[currentInstance.id],
            [itemId]: newResponseData
          }
        };

        // Auto-save to localStorage for instances
        if (assignmentId && !isReadOnly) {
          const localStorageKey = `checklist_${assignmentId}`;
          const localStorageData = {
            instanceResponses: newInstanceResponses,
            lastSaved: new Date().toISOString()
          };
          console.log(`Saving instance responses to localStorage with key: ${localStorageKey}`, localStorageData);
          localStorage.setItem(localStorageKey, JSON.stringify(localStorageData));
        }

        return newInstanceResponses;
      });
    } else {
      // Non-instance response handling (legacy)
      setResponses(prev => {
        let newResponseData = {
          ...prev[itemId],
          item_id: itemId,
          cannot_complete: false,
          cannot_complete_reason: ''
        };

        // Handle different response types
        if (typeof response === 'object' && response !== null && response.file_uploads) {
          // This is a file upload response
          newResponseData = {
            ...newResponseData,
            response: response.response,
            file_uploads: response.file_uploads
          };
        } else {
          // This is a regular response (checkbox, text, etc.)
          newResponseData = {
            ...newResponseData,
            response: response
          };
        }

        const newResponses = {
          ...prev,
          [itemId]: newResponseData
        };

        // Auto-save to localStorage (only if not read-only)
        if (assignmentId && !isReadOnly) {
          const localStorageKey = `checklist_${assignmentId}`;
          const localStorageData = {
            responses: newResponses,
            lastSaved: new Date().toISOString()
          };
          console.log(`Saving to localStorage with key: ${localStorageKey}`, localStorageData);
        localStorage.setItem(localStorageKey, JSON.stringify(localStorageData));
      }

        return newResponses;
      });
    }
    
    // Auto-save to server
    setTimeout(() => saveToServer(), 100);
  }, [assignmentId, isReadOnly, instances, currentInstanceIndex]);

  const handleItemNotes = useCallback((itemId: number, notes: string) => {
    if (isReadOnly) return; // Prevent editing in read-only mode
    
    const currentInstance = getCurrentInstance();
    
    if (currentInstance) {
      // Instance-based notes handling
      setInstanceResponses(prev => ({
        ...prev,
        [currentInstance.id]: {
          ...prev[currentInstance.id],
          [itemId]: {
            ...prev[currentInstance.id]?.[itemId],
            item_id: itemId,
            instance_id: currentInstance.id,
            notes: notes
          }
        }
      }));
    } else {
      // Non-instance notes handling
      setResponses(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          item_id: itemId,
          notes: notes
        }
      }));
    }
    
    // Auto-save to server
    setTimeout(() => saveToServer(), 100);
  }, [isReadOnly, instances, currentInstanceIndex]);

  const handleCannotComplete = useCallback((itemId: number, cannotComplete: boolean, reason: string = '') => {
    if (isReadOnly) return; // Prevent editing in read-only mode
    
    const currentInstance = getCurrentInstance();
    
    if (currentInstance) {
      // Instance-based cannot complete handling
      setInstanceResponses(prev => ({
        ...prev,
        [currentInstance.id]: {
          ...prev[currentInstance.id],
          [itemId]: {
            ...prev[currentInstance.id]?.[itemId],
            item_id: itemId,
            instance_id: currentInstance.id,
            cannot_complete: cannotComplete,
            cannot_complete_reason: reason,
            response: cannotComplete ? null : prev[currentInstance.id]?.[itemId]?.response
          }
        }
      }));
    } else {
      // Non-instance cannot complete handling
      setResponses(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          item_id: itemId,
          cannot_complete: cannotComplete,
          cannot_complete_reason: reason,
          response: cannotComplete ? null : prev[itemId]?.response
        }
      }));
    }
    
    // Auto-save to server
    setTimeout(() => saveToServer(), 100);
  }, [isReadOnly, instances, currentInstanceIndex]);

  const saveToServer = async () => {
    if (!assignmentId || !checklist || isReadOnly) return; // Don't save in read-only mode

    try {
      // Get current responses state
      const currentResponses = responses;
      
      const completionData = {
        assignment: parseInt(assignmentId),
        checklist: checklist.id,
        responses: currentResponses,
        status: isChecklistComplete() ? 'completed' : 'in_progress',
        completion_percentage: getCompletionPercentage()
      };

      // Match this checklist. The list endpoint does not filter on assignment id,
      // and the first row can belong to a different checklist.
      const existingResponse = await api.get(`/checklist-completions/?checklist=${checklist.id}`);
      const rows = existingResponse.data.results || existingResponse.data || [];
      const mine = rows.find((row: { checklist?: number | { id?: number } }) => {
        const checklistId = typeof row.checklist === 'object' ? row.checklist?.id : row.checklist;
        return Number(checklistId) === Number(checklist.id);
      });

      if (mine?.id) {
        await api.patch(`/checklist-completions/${mine.id}/`, completionData);
      } else {
        await api.post('/checklist-completions/', completionData);
      }
    } catch (err) {
      console.error('Error saving to server:', err);
    }
  };

  const handleNext = () => {
    if (isReadOnly) {
      // In read-only mode, just navigate without saving
      if (currentItemIndex < checklist!.items.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
      }
      return;
    }

    // Save before navigating
    setTimeout(() => saveToServer(), 100);
    
    if (currentItemIndex < checklist!.items.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
    } else {
      // At the end of items for this instance
      if (instances.length > 0) {
        const currentInstance = getCurrentInstance();
        if (currentInstance && isInstanceComplete(currentInstance.id)) {
          // Current instance is complete, check if there are more instances
          if (currentInstanceIndex < instances.length - 1) {
            // Auto-advance to next instance
            setCurrentInstanceIndex(currentInstanceIndex + 1);
            setCurrentItemIndex(0);
          } else {
            // All instances complete
            setShowReviewDialog(true);
          }
        } else {
          setShowReviewDialog(true);
        }
      } else {
        setShowReviewDialog(true);
      }
    }
  };

  const handlePrevious = () => {
    if (isReadOnly) {
      // In read-only mode, just navigate without saving
      if (currentItemIndex > 0) {
        setCurrentItemIndex(currentItemIndex - 1);
      }
      return;
    }

    // Save before navigating
    setTimeout(() => saveToServer(), 100);
    
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
    }
  };

  const handleNavigateToInstance = (instanceIndex: number) => {
    // Save current progress before switching instances
    if (!isReadOnly) {
      setTimeout(() => saveToServer(), 100);
    }
    setCurrentInstanceIndex(instanceIndex);
    setCurrentItemIndex(0);
  };

  const handleNavigateToItem = (index: number) => {
    if (isReadOnly) {
      // In read-only mode, just navigate without saving
      setCurrentItemIndex(index);
      return;
    }

    // Save before navigating
    setTimeout(() => saveToServer(), 100);
    
    setCurrentItemIndex(index);
  };

  const handleCompleteChecklist = async () => {
    try {
      // Final save to server with completed status
      if (!assignmentId || !checklist) return;

      const responsesToSave = getCurrentResponses();
      const missingCannotCompleteReason = collectionHasCannotCompleteWithoutReason(responsesToSave)
        || instances.some((instance) => collectionHasCannotCompleteWithoutReason(instanceResponses[instance.id]));
      if (missingCannotCompleteReason) {
        setError('Enter a reason for each item marked Cannot Complete before submitting.');
        setShowReviewDialog(true);
        return;
      }
      setError(null);
      
      const completionData = {
        assignment: parseInt(assignmentId),
        checklist: checklist.id,
        responses: responsesToSave,
        status: 'completed', // Mark as completed
        completion_percentage: getCompletionPercentage()
      };

      // Update the assignment completion record directly
      // This is now the single source of truth for all completion types
      const existingResponse = await api.get(`/assignment-completions/?assignment=${assignmentId}`);
      
      // Clean responses data to remove File objects that can't be serialized
      const cleanResponses: { [key: number]: any } = {};
      Object.entries(responsesToSave).forEach(([itemId, response]) => {
        cleanResponses[parseInt(itemId)] = {
          item_id: response.item_id,
          response: response.response,
          cannot_complete: response.cannot_complete || false,
          cannot_complete_reason: response.cannot_complete_reason || '',
          notes: response.notes || '',
          // Convert file_uploads to a simple boolean or count to avoid serialization issues
          has_file_uploads: response.file_uploads && response.file_uploads.length > 0,
          file_uploads_count: response.file_uploads ? response.file_uploads.length : 0
        };
      });

      // For multi-instance checklists, clean all instance responses
      const cleanInstanceResponses: { [instanceId: number]: { [itemId: number]: any } } = {};
      if (instances.length > 0) {
        instances.forEach(instance => {
          const instResponses = instanceResponses[instance.id] || {};
          const cleaned: { [key: number]: any } = {};
          Object.entries(instResponses).forEach(([itemId, response]: [string, any]) => {
            cleaned[parseInt(itemId)] = {
              item_id: response.item_id,
              response: response.response,
              cannot_complete: response.cannot_complete || false,
              cannot_complete_reason: response.cannot_complete_reason || '',
              notes: response.notes || '',
              has_file_uploads: response.file_uploads && response.file_uploads.length > 0,
              file_uploads_count: response.file_uploads ? response.file_uploads.length : 0
            };
          });
          cleanInstanceResponses[instance.id] = cleaned;
        });
      }

      // Prepare completion data for AssignmentCompletion
      const assignmentCompletionData: any = {
        status: 'completed',
        completed_at: new Date().toISOString(),
        score: getCompletionPercentage(),
        notes: 'Checklist completed successfully',
        completion_data: {
          completed_items: Object.values(responsesToSave).filter((r: any) => checklistResponseCountsAsDone(r)).length,
          total_items: checklist.items.length,
          percentage: getCompletionPercentage(),
          checklist_id: checklist.id
        }
      };

      // Include instanceResponses for multi-instance checklists, otherwise include regular responses
      if (instances.length > 0 && Object.keys(cleanInstanceResponses).length > 0) {
        assignmentCompletionData.completion_data.instanceResponses = cleanInstanceResponses;
      } else {
        assignmentCompletionData.completion_data.responses = cleanResponses;
      }
      
      if (existingResponse.data.results && existingResponse.data.results.length > 0) {
        const completionId = existingResponse.data.results[0].id;
        await api.patch(`/assignment-completions/${completionId}/`, assignmentCompletionData);
      } else {
        // Create new AssignmentCompletion - use the first assignee from the assignment
        const assigneeId = assignment?.assignees?.[0]?.id || 1; // Fallback to 1 if no assignees found
        await api.post('/assignment-completions/', {
          assignment: parseInt(assignmentId),
          assignee_id: assigneeId,
          ...assignmentCompletionData
        });
      }

      // Calculate completion info
      const endTime = new Date();

      // Calculate separate counts for required and optional items
      const requiredItems = checklist?.items.filter(item => item.is_required) || [];
      const optionalItems = checklist?.items.filter(item => !item.is_required) || [];
      
      // If using instances, aggregate across all instances
      let completedRequiredItems = 0;
      let completedOptionalItems = 0;
      let totalItemsCount = 0;
      let totalRequiredCount = 0;
      let totalOptionalCount = 0;

      if (instances.length > 0) {
        // Multi-instance: sum up across all instances
        instances.forEach(instance => {
          const instResponses = instanceResponses[instance.id] || {};
          
          // Count completed required items for this instance
          completedRequiredItems += requiredItems.filter(item => {
            const response = instResponses[item.id];
            return checklistResponseCountsAsDone(response);
          }).length;
          
          // Count completed optional items for this instance
          completedOptionalItems += optionalItems.filter(item => {
            const response = instResponses[item.id];
            return checklistResponseCountsAsDone(response);
          }).length;
        });
        
        totalItemsCount = (checklist?.items.length || 0) * instances.length;
        totalRequiredCount = requiredItems.length * instances.length;
        totalOptionalCount = optionalItems.length * instances.length;
      } else {
        // Single instance: original logic
        const completionResponses = getCurrentResponses();
        completedRequiredItems = requiredItems.filter(item => {
          const response = completionResponses[item.id];
          return checklistResponseCountsAsDone(response);
        }).length;
        
        completedOptionalItems = optionalItems.filter(item => {
          const response = completionResponses[item.id];
          return checklistResponseCountsAsDone(response);
        }).length;
        
        totalItemsCount = checklist?.items.length || 0;
        totalRequiredCount = requiredItems.length;
        totalOptionalCount = optionalItems.length;
      }

      setCompletionResults({
        completedAt: endTime.toISOString(),
        totalItems: totalItemsCount,
        requiredItems: totalRequiredCount,
        completedRequiredItems,
        optionalItems: totalOptionalCount,
        completedOptionalItems,
        percentageComplete: getCompletionPercentage(),
        instancesCompleted: instances.length || 1
      });

      setShowReviewDialog(false);
      setShowCompletionDialog(true);

      // Clear localStorage
      if (assignmentId) {
        localStorage.removeItem(`checklist_${assignmentId}`);
      }

    } catch (err) {
      console.error('Error completing checklist:', err);
      setError('Failed to complete checklist. Please try again.');
    }
  };

  const markAssignmentStarted = async () => {
    if (!assignmentId || !user?.id) return;
    const response = await api.get(`/assignment-completions/?assignment=${assignmentId}&assignee=${user.id}`);
    const results = response.data.results || [];
    const mine = results.find((completion: any) => {
      const assigneeId = completion.assignee?.id ?? completion.assignee_id ?? completion.assignee;
      return Number(assigneeId) === Number(user.id);
    });
    if (!mine || mine.status === 'completed' || mine.status === 'in_progress') return;
    await api.patch(`/assignment-completions/${mine.id}/`, { status: 'in_progress' });
  };

  const handleSaveAndExit = async () => {
    try {
      await saveToServer();
      await markAssignmentStarted();
      navigate('/my-assignments');
    } catch (err) {
      console.error('Error saving and exiting:', err);
      setError('Failed to save progress. Please try again.');
    }
  };

  const handleExitWithoutSaving = () => {
    navigate('/my-assignments');
  };

  // Before unload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave this page? Your progress has been saved and you can always pick up where you left off from the My Assignments page.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading Checklist...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadAssignmentData}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!checklist) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Checklist not found.
        </Alert>
      </Box>
    );
  }

  const currentItem = getCurrentItem();
  if (!currentItem) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No checklist items found.
        </Alert>
      </Box>
    );
  }

  const currentInstance = getCurrentInstance();

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Instance Sidebar - Only show if there are multiple instances */}
      {instances.length > 0 && showInstanceSidebar && (
        <Box sx={{
          width: isMobile ? '100%' : 280,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: isMobile && currentItemIndex >= 0 ? 'none' : 'block',
          overflowY: 'auto'
        }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">
              Instances ({getCompletedInstancesCount()}/{instances.length})
            </Typography>
          </Box>
          <List>
            {instances.map((instance, index) => (
              <ListItem
                key={instance.id}
                button
                selected={currentInstanceIndex === index}
                onClick={() => handleNavigateToInstance(index)}
                sx={{
                  borderLeft: currentInstanceIndex === index ? 4 : 0,
                  borderColor: 'primary.main',
                  bgcolor: currentInstanceIndex === index ? 'action.selected' : 'transparent'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                  {isInstanceComplete(instance.id) ? (
                    <CheckIcon color="success" fontSize="small" />
                  ) : (
                    <Box sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: '50%', 
                      border: 2, 
                      borderColor: 'primary.main'
                    }} />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={currentInstanceIndex === index ? 'bold' : 'normal'}>
                      {instance.instance_identifier || `#${index + 1}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {instance.instance_name}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          zIndex: 1
        }}>
          {/* Top controls row: status + actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isReadOnly && (
                <Chip 
                  label="Completed - Read Only" 
                  color="success" 
                  size="small"
                  icon={<CheckIcon />}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isReadOnly ? (
                <Button
                  variant="outlined"
                  startIcon={<BackIcon />}
                  onClick={() => navigate('/my-assignments')}
                >
                  Back to Assignments
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveAndExit}
                >
                  Save & Exit
                </Button>
              )}
              <IconButton onClick={() => setShowExitDialog(true)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Title row */}
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            {isReadOnly ? `Review: ${checklist.title}` : checklist.title}
          </Typography>
        </Box>

        {/* Instance Info (if applicable) */}
        {currentInstance && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="body2" color="primary.main" fontWeight="bold">
              Instance {currentInstanceIndex + 1} of {instances.length}: {currentInstance.instance_name}
            </Typography>
            {currentInstance.instance_identifier && (
              <Typography variant="caption" color="text.secondary">
                ID: {currentInstance.instance_identifier}
              </Typography>
            )}
            {currentInstance.location_details && (
              <Typography variant="caption" color="text.secondary" display="block">
                {currentInstance.location_details}
              </Typography>
            )}
          </Box>
        )}

        {/* Progress Section (adds horizontal padding to clear divider) */}
        <Box sx={{ px: 3 }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress: {getCompletionPercentage()}% complete
                {instances.length > 0 && ` (Instance ${currentInstanceIndex + 1})`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Item {currentItemIndex + 1} of {checklist.items.length}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={getCompletionPercentage()} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Progress Nodes */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {checklist.items.map((item, index) => {
              const response = currentResponses[item.id];
              const isCompleted = isItemResponseComplete(response, item);
              const isCurrent = index === currentItemIndex;
              
              return (
                <Box
                  key={item.id}
                  onClick={() => handleNavigateToItem(index)}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    border: 2,
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    bgcolor: isCompleted ? 'success.main' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isCompleted ? 'success.dark' : 'action.hover'
                    }
                  }}
                >
                  {isCompleted && (
                    <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

      {/* Main Content */}
      <Box sx={{ p: 3, width: '100%' }}>
        <ChecklistItemRenderer
          key={`${getCurrentInstance()?.id || 'no-instance'}-${currentItem.id}`}
          item={currentItem}
          response={currentResponses[currentItem.id]}
          onResponse={handleItemResponse}
          onNotes={handleItemNotes}
          onCannotComplete={handleCannotComplete}
          isReadOnly={isReadOnly}
        />
        
        {/* Navigation - Now positioned right after the content */}
        <Box sx={{ 
          mt: 4,
          pt: 3, 
          borderTop: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handlePrevious}
            disabled={currentItemIndex === 0}
          >
            Previous
          </Button>

          <Typography variant="body2" color="text.secondary">
            {getCompletionPercentage()}% Complete
          </Typography>

          {isReadOnly ? (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              disabled={currentItemIndex === checklist.items.length - 1}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={currentItemIndex === checklist.items.length - 1 ? <CheckIcon /> : <NextIcon />}
              onClick={handleNext}
            >
              {currentItemIndex === checklist.items.length - 1 ? 'Complete & Review' : 'Next'}
            </Button>
          )}
        </Box>
      </Box>
      </Box>

      {/* Review Dialog */}
      <ChecklistReviewDialog
        open={showReviewDialog}
        onClose={() => setShowReviewDialog(false)}
        onComplete={handleCompleteChecklist}
        checklist={checklist}
        responses={currentResponses}
        instances={instances}
        instanceResponses={instanceResponses}
      />

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitDialog} onClose={() => setShowExitDialog(false)}>
        <DialogTitle>Leave Checklist?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to leave this page? Your progress has been saved and you can always pick up where you left off from the My Assignments page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExitDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleExitWithoutSaving} color="error">
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog open={showCompletionDialog} onClose={() => navigate('/my-assignments')} maxWidth="sm" fullWidth>
        <DialogTitle>
          Checklist Completed!
        </DialogTitle>
        <DialogContent>
          {completionResults && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Congratulations! You have successfully completed the checklist.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Completion Summary
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Required Items:</strong> {completionResults.completedRequiredItems} of {completionResults.requiredItems} completed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Optional Items:</strong> {completionResults.completedOptionalItems} of {completionResults.optionalItems} completed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Completion Rate:</strong> {completionResults.percentageComplete}%
                </Typography>
                {completionResults.instancesCompleted && completionResults.instancesCompleted > 1 && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Instances Completed:</strong> {completionResults.instancesCompleted} of {completionResults.instancesCompleted}
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                <strong>Completed at:</strong> {new Date(completionResults.completedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/my-assignments')} variant="contained">
            Back to Assignments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChecklistPlayer; 